import { forwardRef, type KeyboardEvent } from "react";
import RatingOverlaySelect from "@/elements/RatingOverlaySelect";
import {
   applyRankDigit,
   formatRankDisplay,
   isOverlayRatingInput,
   isValidRank,
   sanitizeRank,
   type RatingInputMode,
   RANK_1_TO_4_MAX,
} from "@/sections/rankInput";

type RankInputControlProps = {
   ratingInput: RatingInputMode;
   value: string | number;
   max?: number;
   showValidations?: boolean;
   invalid?: boolean;
   className?: string;
   inputClassName?: string;
   selectClassName?: string;
   dataId?: number | string;
   tabIndex?: number;
   ariaLabel?: string;
   id?: string;
   name?: string;
   onCommit: (value: number | "") => void;
   onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => void;
   isAdmin?: boolean;
};

const RankInputControl = forwardRef<HTMLInputElement, RankInputControlProps>((props, ref) => {
   const max = props.max ?? RANK_1_TO_4_MAX;
   const ratingInput = props.ratingInput;
   const current = sanitizeRank(props.value, max);
   const display = formatRankDisplay(current, max);
   const invalid = !!props.invalid;
   const inputClass = props.inputClassName || "checklistRatingInput";
   const selectClass = props.selectClassName || "checklistRatingSelect";

   function commit(raw: unknown) {
      if (raw === "" || raw === null || raw === undefined) {
         props.onCommit("");
         return;
      }
      const normalized = sanitizeRank(raw, max);
      props.onCommit(normalized === 0 ? "" : normalized);
   }

   function onTypedKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      props.onKeyDown?.(e);
      if (e.defaultPrevented) return;

      const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (allowedKeys.includes(e.key)) {
         if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault();
            commit("");
         }
         return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
         e.preventDefault();
         return;
      }

      if (!/^[0-9]$/.test(e.key)) {
         e.preventDefault();
         return;
      }

      if (props.isAdmin) return;

      e.preventDefault();
      const next = applyRankDigit(e.key, max);
      commit(next || "");
   }

   function onTypedChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value.slice(-1);
      if (raw === "") {
         commit("");
         return;
      }
      commit(applyRankDigit(raw, max) || "");
   }

   function onTypedPaste(e: React.ClipboardEvent<HTMLInputElement>) {
      e.preventDefault();
      const digits = (e.clipboardData?.getData("text") ?? "").replace(/\D/g, "");
      if (!digits) return;
      commit(applyRankDigit(digits.slice(-1), max) || "");
   }

   function onDropdownChange(e: React.ChangeEvent<HTMLSelectElement>) {
      const raw = e.target.value;
      commit(raw === "" ? "" : Number(raw));
   }

   if (ratingInput === "input") {
      return (
         <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={1}
            id={props.id}
            name={props.name}
            aria-label={props.ariaLabel}
            tabIndex={props.tabIndex}
            data-id={props.dataId}
            className={`${inputClass}${invalid ? " oops invalid" : ""}${props.className ? ` ${props.className}` : ""}`}
            value={display}
            onChange={onTypedChange}
            onKeyDown={onTypedKeyDown}
            onPaste={onTypedPaste}
            onFocus={(e) => e.target.select()}
            onWheel={(e) => e.currentTarget.blur()}
         />
      );
   }

   if (ratingInput === "dropdown") {
      return (
         <select
            id={props.id}
            name={props.name}
            aria-label={props.ariaLabel}
            tabIndex={props.tabIndex}
            data-id={props.dataId}
            className={`${selectClass}${invalid ? " oops invalid" : ""}${props.className ? ` ${props.className}` : ""}`}
            value={isValidRank(current, max) ? String(current) : ""}
            onChange={onDropdownChange}
            onKeyDown={(e) => props.onKeyDown?.(e)}
         >
            <option value="" />
            {Array.from({ length: max }, (_, index) => index + 1).map((optionValue) => (
               <option key={optionValue} value={optionValue}>{optionValue}</option>
            ))}
         </select>
      );
   }

   if (isOverlayRatingInput(ratingInput)) {
      return (
         <RatingOverlaySelect
            ref={ref}
            value={current}
            max={max}
            orientation={ratingInput}
            showValidations={props.showValidations}
            inputClassName={inputClass}
            ariaLabel={props.ariaLabel}
            tabIndex={props.tabIndex}
            dataId={props.dataId}
            onSelect={(value) => commit(value)}
            onKeyDown={props.onKeyDown}
         />
      );
   }

   return null;
});

RankInputControl.displayName = "RankInputControl";

export default RankInputControl;
